/* AddOrUpdateFdRisks.tsx — FULL FILE with duplicate-proof save logic + verbose logs */

import React, {useEffect, useMemo, useState} from 'react';
import {Route} from 'react-router-dom';
import {
  reduxToggleSidePanel,
  reduxSetSidePanelEditState,
} from 'appredux/actions';
import CheckIcon from 'components/CheckIcon/CheckIcon';
import {
  apiGetAllFarmRisks,
  apiGetFarmRisksByApplicationId,
  apiCreateFdFarmRisk,
} from 'constants/Api';
import FdFarmRiskForm, {
  RiskRow,
  Submission,
} from 'components/applications/FdFarmRiskForm';

/* ───────── local types ───────── */
type FarmRiskBasic = {id: number; name: string};
type FarmRiskByApp = {
  id: number;
  mitigation: string;
  problem: string;
  risk: {id: number; name: string};
};

type Props = {applicationId: number};

/* ───────── component ───────── */
const AddOrUpdateFdRisks: React.FC<Props> = ({applicationId}) => {
  const [masterRisks, setMasterRisks] = useState<{id: string; name: string}[]>(
    [],
  );
  const [savedRows, setSavedRows] = useState<FarmRiskByApp[]>([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  /* ---------- fetch master risks ---------- */
  useEffect(() => {
    apiGetAllFarmRisks()
      .then((list: FarmRiskBasic[]) => {
        const mapped = list.map((r) => ({id: String(r.id), name: r.name}));
        setMasterRisks(mapped);
      })
      .catch((e) => console.error('🔴 master risks error', e));
  }, []);

  /* ---------- fetch existing rows ---------- */
  useEffect(() => {
    apiGetFarmRisksByApplicationId(applicationId)
      .then((rows) => {
        setSavedRows(rows);
      })
      .catch((e) => console.error('🔴 saved rows error', e));
  }, [applicationId]);

  /* ---------- dedupe for form display ---------- */
  const uniqueSaved = useMemo(() => {
    const seen = new Set<string>();
    const out = savedRows.filter((r) => {
      const key = String(r.risk.id);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    return out;
  }, [savedRows]);

  /* ---------- build default rows ---------- */
  const defaultRows: RiskRow[] = useMemo(
    () =>
      uniqueSaved.map((r) => ({
        id: r.id,
        riskId: String(r.risk.id),
        problem: r.problem,
        mitigation: r.mitigation,
      })),
    [uniqueSaved],
  );

  /* map<riskId, existingRow> for quick diff */
  const mapByRiskId = useMemo(() => {
    const m = new Map<string, FarmRiskByApp>();
    uniqueSaved.forEach((r) => m.set(String(r.risk.id), r));
    return m;
  }, [uniqueSaved]);

  /* ---------- submit ---------- */
  const submit = async (data: Submission) => {
    setLoading(true);

    /* first occurrence per risk in UI */
    const firstPerRisk = new Map<string, RiskRow>();
    data.rows.forEach((row) => {
      const key = String(row.riskId);
      if (!firstPerRisk.has(key)) firstPerRisk.set(key, row);
    });

    const ops: Promise<any>[] = [];

    firstPerRisk.forEach((row, key) => {
      const existing = mapByRiskId.get(key);

      /* create new */
      if (!existing) {
        ops.push(
          apiCreateFdFarmRisk({
            riskId: Number(key),
            problem: row.problem.trim(),
            mitigation: row.mitigation.trim(),
            applicationId,
          }),
        );
        return;
      }

      /* update only if changed */
      const changed =
        existing.problem.trim() !== row.problem.trim() ||
        existing.mitigation.trim() !== row.mitigation.trim();

      if (changed) {
        ops.push(
          apiCreateFdFarmRisk({
            id: existing.id,
            riskId: Number(key),
            problem: row.problem.trim(),
            mitigation: row.mitigation.trim(),
            applicationId,
          }),
        );
      } else {
      }
    });

    await Promise.allSettled(ops);

    setSuccess(true);
    reduxSetSidePanelEditState('saved');
    setLoading(false);
  };

  /* ---------- helpers ---------- */
  const close = () => reduxToggleSidePanel(false, null);
  const formKey = defaultRows.map((r) => r.id).join('-') || 'empty';

  /* ---------- success splash ---------- */
  if (success) {
    return (
      <Route
        render={() => (
          <div className="side-panel-success-container">
            <div className="mid-section">
              <div className="success-gif">
                <CheckIcon />
              </div>
            </div>
            <div className="btn-container">
              <button
                className="primary-btn"
                onClick={() => reduxToggleSidePanel(false, null)}
              >
                Done
              </button>
            </div>
          </div>
        )}
      />
    );
  }

  /* ---------- form ---------- */
  return (
    <FdFarmRiskForm
      key={formKey}
      risks={masterRisks}
      defaultRows={defaultRows}
      isLoading={loading}
      onSubmit={submit}
      onCancel={close}
    />
  );
};

export default AddOrUpdateFdRisks;
